import React from 'react';
import { Typography, Box } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';
import ChartLegend from './ChartLegend';
import { fontSizes } from '../../theme/theme';

interface StackedHistogramData {
  [key: string]: string | number;
}

interface StackedHistogramProps extends ChartHeaderProps {
  data: any;
  dataKeys: string[];
  xAxisKey: string;
  colors?: string[];
  height?: number;
  graphHeight?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  barCategoryGap?: string;
  optimalRange?: { min: string; max: string };
  showSummary?: boolean;
  onDetailsClick?: (data?: any) => void;
}

const StackedHistogram: React.FC<StackedHistogramProps> = ({
  title,
  description,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981'],
  height = 350,
  graphHeight = 350,
  xAxisTitle,
  yAxisTitle,
  barCategoryGap = '1%',
  optimalRange,
  details,
  onDetailsClick
}) => {
  // Extract chart data and metadata
  const chartData = data?.chartData || data || [];
  
  const handleBarClick = (data: any, dataKey: string) => {
    if (onDetailsClick) {
      onDetailsClick({ dataKey, dataRow: data });
    }
    console.log('Clicked Bar Data:');
    console.log('Series Key:', dataKey);
    console.log('Category:', data[xAxisKey]);
    console.log('Value:', data[dataKey]);
    console.log('Full Data:', data);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '8px 12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} sx={{ fontSize: '12px', color: entry.color }}>
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <ChartCardWrapper sx={{ height: height }}>
      <ChartHeader title={title} description={description} details={details} />
      <ResponsiveContainer width="100%" height={graphHeight}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          barCategoryGap={barCategoryGap}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={xAxisTitle ? {
              value: xAxisTitle,
              position: 'insideBottom',
              offset: -15,
              style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151', fontWeight: 600 }
            } : undefined}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={yAxisTitle ? {
              value: yAxisTitle,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' }
            } : undefined}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            height={36}
            iconType="rect"
            wrapperStyle={{ fontSize: `${fontSizes.graph.axis}px`, paddingBottom: '10px' }}
          />

          {/* Optimal range indicator */}
          {optimalRange && (
            <>
              <ReferenceLine
                segment={[
                  { x: optimalRange.min, y: 0 },
                  { x: optimalRange.min, y: 250 }
                ]}
                stroke="#10B981"
                strokeWidth={0}
              />
              <ReferenceLine
                segment={[
                  { x: optimalRange.max, y: 0 },
                  { x: optimalRange.max, y: 250 }
                ]}
                stroke="#10B981"
                strokeWidth={0}
              />
            </>
          )}

          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={colors[index % colors.length]}
              radius={index === dataKeys.length - 1 ? [4, 4, 0, 0] : undefined}
              onClick={(data) => handleBarClick(data, key)}
              cursor="pointer"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <ChartLegend 
        bottomDescription={details?.bottomDescription}
        subPoints={details?.subPoints}
        colors={colors}
      />
    </ChartCardWrapper>
  );
};

export default StackedHistogram;
